'use client';

import React from 'react'

import { clsx } from 'clsx';
import { getFileUrl, getRouteFunctions, getTranslationFunction } from '@/app/utils/mixed_csr_ssr';

import Container from '@/components/shared/Container'
import Typography from '@/components/shared/Typography'
import LinkButton from '@/components/shared/forms/LinkButton';
import Image from 'next/image'

export default function CardGridSection({ params, cards, backgroundPosition = 'right', className = '' }) {
    const objectPositionClass = {
        'bottom': 'object-bottom',
        'bottom-left': 'object-bottom-left',
        'bottom-right': 'object-bottom-right',
        'center': 'object-center',
        'left': 'object-left',
        'right': 'object-right',
        'top': 'object-top',
        'top-left': 'object-top-left',
        'top-right': 'object-top-right',
    }[backgroundPosition] ?? 'object-center';

    const { config, locale } = params;
    const { route, replacePathsWithRoutes } = getRouteFunctions(config, locale);
    const { t } = getTranslationFunction(config, locale);

    const content = (card) => {
        return (
            <>
                <div className='w-full h-full absolute inset-0'>
                    {card.header_media && (
                        <>
                            {card.header_media.type.includes('video') && (
                                <video alt={card.header_media.title ?? 'Hero video'} autoPlay loop muted playsInline className='w-full h-full object-cover'>
                                    <source src={getFileUrl(card.header_media.id)} type={card.header_media.type} />
                                </video>
                            )}
                            {card.header_media.type.includes('image') && (
                                <>
                                    <Image src={getFileUrl(card.header_media.id)} alt={card.header_media.title ?? 'Hero image'} width={1920} height={1080} className={`w-full h-full object-cover ${objectPositionClass}`} />
                                </>
                            )}

                            {card.text_variant === 'light' && (
                                <div className='absolute inset-0 w-full h-full bg-black/30'></div>
                            )}
                        </>
                    )}
                </div>
                
                <div className='relative inset-0 w-full h-full z-10 px-6 py-10 flex flex-col gap-2'>
                    {card.translated_content ? (
                        <>
                            {card.translated_content.title && (
                                <Typography variant='h2' styleVariant='h3' className={card.text_variant === 'light' ? 'text-secondary-100' : ''}>
                                    {card.translated_content.title}
                                </Typography>
                            )}
                            {card.translated_content.subtitle && (
                                <Typography variant='h6' className={card.text_variant === 'light' ? 'text-secondary-100' : ''}>
                                    {card.translated_content.subtitle}
                                </Typography>
                            )}
                            {card.translated_content.text && (
                                <Typography variant='p' className={`${card.text_variant === 'dark' ? 'text-primary-800' : ''} w-full lg:w-2/3`}>
                                    {card.translated_content.text}
                                </Typography>
                            )}
                            {card.translated_content.short_description && (
                                <Typography variant='p' className={`${card.text_variant === 'dark' ? 'text-primary-800' : ''} w-full lg:w-2/3`}>
                                    {card.translated_content.short_description}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            {card.title && (
                                <Typography variant='h2' styleVariant='h3' className={card.text_variant === 'light' ? 'text-secondary-100' : ''}>
                                    {card.title}
                                </Typography>
                            )}
                            {card.subtitle && (
                                <Typography variant='h6' className={card.text_variant === 'light' ? 'text-secondary-100' : ''}>
                                    {card.subtitle}
                                </Typography>
                            )}

                            {card.markdown ? (
                                <>
                                    <div className={`${card.text_variant === 'dark' ? 'text-primary-800' : ''} w-2/3`} dangerouslySetInnerHTML={{ __html: replacePathsWithRoutes(card.markdown, params) }}></div>
                                </>
                            ) : (
                                <>
                                    {card.text && (
                                        <Typography variant='p' className={`${card.text_variant === 'dark' ? 'text-primary-800' : ''} w-2/3`}>
                                            {card.text}
                                        </Typography>
                                    )}
                                </>
                            )}    
                        </>
                    )}

                    {(card.link || card.slug) ? (
                        <LinkButton href={route(card.link ? card.link : card.slug ?? '')} variant={card.text_variant === 'light' ? 'secondary-bordered' : 'primary-bordered'} wrapperClassName='mt-auto'>
                            {t('read_more')}
                        </LinkButton>
                    ) : (
                        null
                    )}
                </div>
            </>
        )
    }
    
    return (
		<Container className={clsx('pb-20 md:px-10 lg:px-20 -mt-28 lg:-mt-32 relative z-10 text-white', className)}>
			<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {cards.map((card, index) => {
                    return (
                        <div key={index} style={{
                            height: '450px',
                            overflowY: 'auto',
                        }} className={`h-min bg-secondary-100/80 flex flex-col justify-center items-center rounded-4xl overflow-hidden relative ${(index % 2 === 0 && index === cards.length - 1) ? 'lg:col-start-2' : ''}`}>
                            {(card.link || card.slug) ? (
                                <div className='block w-full h-full cursor-pointer' onClick={() => window.location.href = route(card.link ? card.link : card.slug ?? '')}>
                                    {content(card)}
                                </div>
                            ) : (
                                content(card)
                            )}
                        </div>
					)
				})}
			</div>
		</Container>

	)
}
